/**
 * Hosted Agent Hooks
 *
 * React Query hooks for hosted agent CRUD, health checks, and diagnostics.
 * Uses connections.json hostedAgentConnections for persistence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useConnectionsJson, useSaveConnectionsJson, useSaveDraftConnectionsJson } from '@/hooks/useConnections';
import { useAppStore } from '@/stores/appStore';
import { useConnectionStore } from '@/stores/connectionStore';
import type {
  HealthCheckResponse,
  HostedAgentConnection,
  HostedAgentStatus,
  SetupProgress,
} from '@/types/hostedAgent';
import {
  runDiagnostics,
  runHealthCheck,
  type DiagnosticResults,
} from '@/services/hostedAgentService';
import { isLocalMode } from '@/services/localRuntimeClient';
import { useMemo } from 'react';
import { telemetryService } from '@/services/telemetryService';

// =============================================================================
// SHARED HELPERS
// =============================================================================

/**
 * Builds the appInfo needed by useConnectionsJson/useSaveConnectionsJson.
 * Uses natural prerequisites: if a project/app is selected (cloud mode), use it.
 *
 * When nothing is selected yet:
 *  - Local mode: fall back to synthetic 'local' info so the query fires — the
 *    service layer routes to /local/connections when isLocalMode() is true.
 *  - Cloud mode: return null so useConnectionsJson stays disabled (enabled gate
 *    requires projectName/appName) instead of firing a bogus request against
 *    /automationProjects/local/applications/local/... during initial load.
 */
function useAppInfoForConnections() {
  const { selectedProject, selectedApp } = useAppStore();

  if (selectedProject && selectedApp) {
    return { projectName: selectedProject, appName: selectedApp.name, appId: selectedApp.properties.appResourceId };
  }

  // Only synthesize 'local' info in local mode; in cloud mode wait for real app context.
  if (isLocalMode()) {
    return { projectName: 'local', appName: 'local', appId: 'local' };
  }

  return null;
}

/**
 * Resolve the current connections config for a mutation, refetching if the
 * React Query cache hasn't populated yet.
 *
 * The hosted-agent mutations persist the *whole* connections.json (spreading
 * the current config + their hostedAgentConnections change). If we seed that
 * from an empty baseline while the cache is still loading (`data === undefined`),
 * we'd overwrite connections.json with a file that drops every other connection
 * type (managed APIs, service providers, MCP, etc.) — silent data loss.
 *
 * A loaded-but-empty config (`{}`) is a valid baseline and returned as-is; only
 * `undefined` (not yet loaded) triggers a refetch. If it still can't load, throw
 * so the caller aborts rather than persisting an incomplete file.
 *
 * Exported for unit testing.
 */
export async function resolveConnectionsConfig<T>(
  query: { data: T | undefined; refetch: () => Promise<{ data: T | undefined }> },
): Promise<T> {
  if (query.data !== undefined) return query.data;
  const refetched = await query.refetch();
  if (refetched.data !== undefined) return refetched.data;
  throw new Error(
    'Unable to load the current connections configuration; aborting to avoid overwriting existing connections.',
  );
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const hostedAgentKeys = {
  all: ['hostedAgents'] as const,
  list: () => [...hostedAgentKeys.all, 'list'] as const,
  detail: (name: string) => [...hostedAgentKeys.all, 'detail', name] as const,
  health: (name: string) => [...hostedAgentKeys.all, 'health', name] as const,
  diagnostics: (name: string) => [...hostedAgentKeys.all, 'diagnostics', name] as const,
};

// =============================================================================
// NORMALIZATION HELPERS
// =============================================================================

const KNOWN_STATUSES = [
  'connected', 'provisioning', 'auth_failed', 'agent_error',
  'version_mismatch', 'unreachable', 'unknown',
] as const satisfies readonly HostedAgentStatus[];

const KNOWN_SETUP_PROGRESS = [
  'agent_created', 'identities_discovered', 'rbac_assigned', 'complete',
] as const satisfies readonly SetupProgress[];

/**
 * connections.json is user-/backend-authored, so status/setupProgress casing and
 * surrounding whitespace may not match the exact lowercase unions (e.g. "Connected",
 * "Complete", "RBAC_Assigned", "connected\n"). Trim + lowercase on load so the
 * badge/label mappings, status switches, and health-poll gating stay resilient to
 * casing/whitespace differences and older data. Unrecognized values fall back to a
 * safe default rather than an unmapped value that renders blank.
 */
function normalizeStatus(raw: unknown): HostedAgentStatus {
  const value = String(raw ?? '').trim().toLowerCase();
  return (KNOWN_STATUSES as readonly string[]).includes(value)
    ? (value as HostedAgentStatus)
    : 'unknown';
}

function normalizeSetupProgress(raw: unknown): SetupProgress {
  const value = String(raw ?? '').trim().toLowerCase();
  // Unknown values fall back to the earliest state (SetupProgress has no 'unknown'
  // sentinel like HostedAgentStatus does) so deriveStatus() keeps the agent in
  // 'provisioning' until a recognized progress value is stored, rather than
  // optimistically treating an unknown/new/typo'd value as fully set up.
  return (KNOWN_SETUP_PROGRESS as readonly string[]).includes(value)
    ? (value as SetupProgress)
    : 'agent_created';
}

// =============================================================================
// READ HOOKS
// =============================================================================

/**
 * Get all hosted agent connections from connections.json
 */
export function useHostedAgents() {
  const appInfo = useAppInfoForConnections();
  const connectionsQuery = useConnectionsJson(appInfo);

  const agents = useMemo(() => {
    const config = connectionsQuery.data;
    if (!config?.hostedAgentConnections) return [];
    return Object.entries(config.hostedAgentConnections).map(([key, conn]) => ({
      key,
      ...conn,
      // connections.json is externally authored — normalize casing so status/label
      // mappings and health-poll gating are resilient to values like "Connected".
      status: normalizeStatus(conn.status),
      setupProgress: normalizeSetupProgress(conn.setupProgress),
    }));
  }, [connectionsQuery.data]);

  return {
    agents,
    isLoading: connectionsQuery.isLoading,
    error: connectionsQuery.error,
    refetch: connectionsQuery.refetch,
  };
}

/**
 * Get a single hosted agent by name from connections.json
 */
export function useHostedAgent(agentName: string | undefined) {
  const { agents, isLoading, error } = useHostedAgents();

  const agent = useMemo(
    () => agents.find((a) => a.configuration.agentName === agentName),
    [agents, agentName],
  );

  return { agent, isLoading, error };
}

// =============================================================================
// HEALTH CHECK HOOK
// =============================================================================

/**
 * Poll health status for a hosted agent.
 * Only polls when agent is in 'connected' or 'provisioning' status.
 */
export function useHostedAgentHealth(connection: HostedAgentConnection | undefined) {
  const { getFoundryToken } = useAuth();

  return useQuery({
    queryKey: hostedAgentKeys.health(connection?.configuration.agentName ?? ''),
    queryFn: async () => {
      if (!connection) throw new Error('No connection');
      const token = await getFoundryToken();
      if (!token) throw new Error('No Foundry token');
      return runHealthCheck(
        token,
        connection.endpoints.invocationsUrl,
        connection.configuration.imageTag,
      );
    },
    enabled: !!connection && (connection.status === 'connected' || connection.status === 'provisioning'),
    refetchInterval: connection?.status === 'provisioning' ? 15_000 : 60_000,
    staleTime: 10_000,
  });
}

// =============================================================================
// DIAGNOSTICS HOOK
// =============================================================================

/**
 * Run diagnostics for a hosted agent. Not auto-fetched — triggered on demand.
 */
export function useHostedAgentDiagnostics(connection: HostedAgentConnection | undefined) {
  const { getArmToken, getFoundryToken, getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();

  return useQuery({
    queryKey: hostedAgentKeys.diagnostics(connection?.configuration.agentName ?? ''),
    queryFn: async (): Promise<DiagnosticResults> => {
      if (!connection) throw new Error('No connection');
      const [armToken, foundryToken, ottoToken] = await Promise.all([getArmToken(), getFoundryToken(), getOttoToken()]);
      if (!armToken || !foundryToken) throw new Error('Missing tokens');

      const appContext = ottoToken && selectedProject && selectedApp
        ? { ottoToken, projectName: selectedProject, appName: selectedApp.name }
        : undefined;

      return runDiagnostics(armToken, foundryToken, connection, appContext);
    },
    enabled: false, // Only runs when manually refetched
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Save a hosted agent connection to connections.json AND connections-draft.json.
 * Both must be updated so the designer (which reads draft) sees the new connection
 * immediately, while production connections.json is also kept in sync.
 */
export function useSaveHostedAgent() {
  const appInfo = useAppInfoForConnections();
  const connectionsQuery = useConnectionsJson(appInfo);
  const saveConnections = useSaveConnectionsJson(appInfo);
  const saveDraftConnections = useSaveDraftConnectionsJson(appInfo);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { key: string; connection: HostedAgentConnection }) => {
      const currentConfig = await resolveConnectionsConfig(connectionsQuery);
      const updated = {
        ...currentConfig,
        hostedAgentConnections: {
          ...currentConfig.hostedAgentConnections,
          [params.key]: params.connection,
        },
      };
      await Promise.all([
        saveConnections.mutateAsync(updated),
        saveDraftConnections.mutateAsync(updated),
      ]);
      // Keep the connection store baseline in sync so a later workflow save
      // (exportToConnectionsConfig seeds hostedAgentConnections from
      // originalConnectionsConfig) doesn't drop this out-of-band change.
      useConnectionStore.getState().setHostedAgentConnectionRecords(updated.hostedAgentConnections ?? {});
      telemetryService.trackEvent('hostedAgent.saved', {
        agentName: params.connection.configuration.agentName,
        status: params.connection.status,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostedAgentKeys.all });
    },
  });
}

/**
 * Update a hosted agent connection's status or progress
 */
export function useUpdateHostedAgent() {
  const appInfo = useAppInfoForConnections();
  const connectionsQuery = useConnectionsJson(appInfo);
  const saveConnections = useSaveConnectionsJson(appInfo);
  const saveDraftConnections = useSaveDraftConnectionsJson(appInfo);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { key: string; updates: Partial<HostedAgentConnection> }) => {
      const currentConfig = await resolveConnectionsConfig(connectionsQuery);
      const existing = currentConfig.hostedAgentConnections?.[params.key];
      if (!existing) throw new Error(`Hosted agent ${params.key} not found`);

      const updated = {
        ...currentConfig,
        hostedAgentConnections: {
          ...currentConfig.hostedAgentConnections,
          [params.key]: { ...existing, ...params.updates },
        },
      };
      await Promise.all([
        saveConnections.mutateAsync(updated),
        saveDraftConnections.mutateAsync(updated),
      ]);
      useConnectionStore.getState().setHostedAgentConnectionRecords(updated.hostedAgentConnections ?? {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostedAgentKeys.all });
    },
  });
}

/**
 * Delete a hosted agent connection from connections.json
 */
export function useDeleteHostedAgent() {
  const appInfo = useAppInfoForConnections();
  const connectionsQuery = useConnectionsJson(appInfo);
  const saveConnections = useSaveConnectionsJson(appInfo);
  const saveDraftConnections = useSaveDraftConnectionsJson(appInfo);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string) => {
      const currentConfig = await resolveConnectionsConfig(connectionsQuery);
      const { [key]: _removed, ...rest } = currentConfig.hostedAgentConnections ?? {};
      const updated = {
        ...currentConfig,
        hostedAgentConnections: rest,
      };
      await Promise.all([
        saveConnections.mutateAsync(updated),
        saveDraftConnections.mutateAsync(updated),
      ]);
      useConnectionStore.getState().setHostedAgentConnectionRecords(updated.hostedAgentConnections ?? {});
      telemetryService.trackEvent('hostedAgent.deleted', { key });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostedAgentKeys.all });
    },
  });
}

// =============================================================================
// STATUS HELPERS
// =============================================================================

/**
 * Map a health-check result to a HostedAgentStatus.
 *
 * This must be able to produce every post-provisioning status the UI actually
 * renders (see HostedAgentStatus). In particular `auth_failed` and `unreachable`
 * are first-class states with their own badges and card/panel affordances, so we
 * derive them here rather than leaving them as dead states only the backend could
 * ever set:
 *   - `auth_failed` — the container responded but the Logic App managed identity
 *                     could not authenticate to Foundry (HTTP 401/403, surfaced by
 *                     runHealthCheck as `model_auth: "auth_failed: HTTP ..."`).
 *   - `unreachable` — the health request itself failed (network error, timeout,
 *                     404/5xx), i.e. the container could not be reached at all.
 */
export function deriveStatus(
  health: HealthCheckResponse | undefined,
  setupProgress: string,
  healthErrored = false,
): HostedAgentStatus {
  // Agent is usable once RBAC is assigned or setup is fully complete
  if (setupProgress !== 'complete' && setupProgress !== 'rbac_assigned') return 'provisioning';
  // The health request threw (after react-query retries) → container unreachable.
  if (healthErrored) return 'unreachable';
  // Container answered but the MI → Foundry token was rejected.
  if (health?.model_auth?.startsWith('auth_failed')) return 'auth_failed';
  switch (health?.status) {
    case 'healthy': return 'connected';
    case 'version_mismatch': return 'version_mismatch';
    case 'unhealthy': return 'agent_error';
    default: return 'unknown';
  }
}
